import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import AuthPage from './pages/Auth/AuthPage'
import PasswordResetPage from './pages/Auth/PasswordResetPage'
import AcceptInvitePage from './pages/Auth/AcceptInvitePage'
import VerifyEmailPage from './pages/Auth/VerifyEmailPage'
import AffiliateApplication from './pages/Auth/AffiliateApplication'
import ProfessionalDashboard from './pages/Affiliate/ProfessionalDashboard'
import ProfessionalPayments from './pages/Affiliate/ProfessionalPayments'
import PromoMaterialsPage from './pages/Affiliate/PromoMaterialsPage'
import SettingsPage from './pages/Settings/SettingsPage'
import AnalyticsPage from './pages/Analytics/AnalyticsPage'
import HelpPage from './pages/Help/HelpPage'

// Protected Route Component.
//
// AuthContext seeds `user` synchronously from localStorage on mount, so
// for a returning user isAuthenticated is already true on the very first
// render -- `loading` just means the real session check is still
// confirming that in the background. Blocking on `loading` regardless
// meant every navigation/reload showed a full-page spinner (replacing the
// entire app, including the header/nav) even though we already knew who
// the user was. Render immediately whenever we have that optimistic
// isAuthenticated state, and only fall back to a loading gate for the
// genuinely unknown case (no cached user yet, first load).
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (!isAuthenticated && loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-black/10 dark:border-white/10 border-t-accent rounded-full animate-spin" />
      </div>
    )
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/auth" replace />
}

// Each page is its own separate route element (no shared scrollable layout
// container), so the browser just keeps whatever scroll position the old
// page was at instead of resetting -- React Router doesn't do this itself
// client-side the way a full page navigation would.
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

function AppRoutes() {
  const { isAuthenticated } = useAuth()

  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Auth Routes */}
      <Route path="/auth" element={isAuthenticated ? <Navigate to="/affiliate/dashboard" replace /> : <AuthPage />} />
      <Route path="/auth/reset-password" element={<PasswordResetPage />} />
      <Route path="/invite" element={<AcceptInvitePage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/apply" element={<AffiliateApplication />} />

      {/* Redirect root based on auth status */}
      <Route path="/" element={<Navigate to={isAuthenticated ? "/affiliate/dashboard" : "/auth"} replace />} />

      {/* Protected Affiliate Routes */}
      <Route path="/affiliate/dashboard" element={
        <ProtectedRoute>
          <ProfessionalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/affiliate/payments" element={
        <ProtectedRoute>
          <ProfessionalPayments />
        </ProtectedRoute>
      } />
      <Route path="/affiliate/promo-materials" element={
        <ProtectedRoute>
          <PromoMaterialsPage />
        </ProtectedRoute>
      } />
      <Route path="/affiliate/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/affiliate/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/affiliate/help" element={
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      } />
    </Routes>
    </>
  )
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  )
}

export default App